import React, { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Empty, Flex, Select, Spin, Table, Tag, Typography } from 'antd'
import type { TableProps } from 'antd'
import { LinkOutlined, SearchOutlined } from '@ant-design/icons'
import {
  type BidOutcome,
  formatCents,
  mockBidOutcomesByPartnerId,
} from '@/data'
import { usePartnerSearch } from '@/utils/usePartnerSearch'

const { Title, Text } = Typography

// TODO: replace with the real bidding Metabase URL.
const METABASE_PLACEHOLDER_URL = 'https://reporting.turbodevhub.com/'

const columns: TableProps<BidOutcome>['columns'] = [
  {
    title: 'When',
    dataIndex: 'created_at',
    key: 'created_at',
    render: (value: string) =>
      value ? new Date(value).toLocaleString('en-US') : '—',
  },
  {
    title: 'Bid',
    dataIndex: 'bid_name',
    key: 'bid_name',
    render: (name: string | null) => name ?? <Text type="secondary">—</Text>,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: BidOutcome['status']) => (
      <Tag color={status === 'accepted' ? 'green' : 'red'}>
        {status === 'accepted' ? 'Accepted' : 'Rejected'}
      </Tag>
    ),
  },
  {
    title: 'Amount',
    dataIndex: 'amount_cents',
    key: 'amount_cents',
    render: (cents: number | null) => formatCents(cents),
  },
  {
    title: 'Reason',
    dataIndex: 'reason',
    key: 'reason',
  },
  {
    title: 'Lead ID',
    dataIndex: 'lead_unique_id',
    key: 'lead_unique_id',
    render: (value: string) => (
      <Text code style={{ fontSize: 11 }}>
        {value.slice(0, 8)}…
      </Text>
    ),
  },
]

export const BiddingOutcomes: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const partnerParam =
    searchParams.get('partner') ?? searchParams.get('campaign')
  const initialId = (() => {
    if (partnerParam == null) return undefined
    const n = Number(partnerParam)
    return Number.isFinite(n) ? n : undefined
  })()

  const [selectedId, setSelectedId] = useState<number | undefined>(initialId)
  const partnerSearchOptions = useMemo(
    () => ({ extraFilters: [{ key: 'has_ladder', value: '1' }] }),
    [],
  )
  const { options, loading, hasMore, onSearch, loadMore } =
    usePartnerSearch(partnerSearchOptions)

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement
      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 40
      if (nearBottom) loadMore()
    },
    [loadMore],
  )

  const handleChange = (id: number | undefined) => {
    setSelectedId(id)
    if (id == null) {
      searchParams.delete('partner')
      searchParams.delete('campaign')
      setSearchParams(searchParams, { replace: true })
    } else {
      setSearchParams({ partner: String(id) }, { replace: true })
    }
  }

  const outcomes = useMemo<BidOutcome[]>(() => {
    if (selectedId == null) return []
    return mockBidOutcomesByPartnerId[selectedId] ?? []
  }, [selectedId])

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Title level={4} style={{ margin: 0 }}>
          Bid Outcomes
        </Title>
        <Text type="secondary">
          Inspect recent bid outcomes by partner. Pick a partner to see the last
          bid attempts.
        </Text>

        <Flex align="center" gap="middle">
          <Text strong>Partner:</Text>
          <Select<number>
            showSearch
            allowClear
            value={selectedId}
            onChange={handleChange}
            onSearch={onSearch}
            onPopupScroll={handleScroll}
            placeholder="Search by name or ID..."
            style={{ minWidth: 320 }}
            loading={loading}
            suffixIcon={<SearchOutlined />}
            filterOption={false}
            options={options}
            notFoundContent={loading ? <Spin size="small" /> : 'No matches'}
            dropdownRender={(menu) => (
              <>
                {menu}
                {loading && options.length > 0 && (
                  <Flex justify="center" style={{ padding: 8 }}>
                    <Spin size="small" />
                  </Flex>
                )}
                {!hasMore && options.length > 0 && (
                  <Flex justify="center" style={{ padding: '4px 0' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      All results loaded
                    </Text>
                  </Flex>
                )}
              </>
            )}
          />
        </Flex>

        {selectedId == null ? (
          <Empty description="Pick a partner to view outcomes." />
        ) : (
          <>
            <Flex justify="space-between" align="center">
              <Text type="secondary">
                The last {outcomes.length || 0} bid attempts. Open Metabase for
                the full history.
              </Text>
              <Button
                type="link"
                icon={<LinkOutlined />}
                href={METABASE_PLACEHOLDER_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Metabase
              </Button>
            </Flex>

            {outcomes.length === 0 ? (
              <Empty description="No outcomes recorded yet." />
            ) : (
              <Table<BidOutcome>
                rowKey="id"
                columns={columns}
                dataSource={outcomes}
                pagination={false}
                size="small"
              />
            )}
          </>
        )}
      </Flex>
    </div>
  )
}
